<template>
  <div
    ref="galleryRoot"
    class="vf-m-mobile-photo-gallery"
    :class="{
      'is-dragging': dragging,
      'is-zooming': zoomedIndex !== null,
      'is-panning': zoomPanning,
    }"
    data-page-swipe-ignore
    tabindex="0"
    role="region"
    aria-label="Product photos and videos"
    aria-describedby="mobile-gallery-keyboard-instructions"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend.passive="onTouchEnd"
    @touchcancel.passive="onTouchEnd"
    @mousedown="onMouseDown"
    @keydown="onGalleryKeydown"
  >
    <span
      id="mobile-gallery-keyboard-instructions"
      class="gallery-keyboard-hint"
    >
      Use Left and Right Arrow keys to change media. Press Enter or Space to
      zoom a photo, and Escape to reset zoom.
    </span>
    <!-- The track is a horizontal scroller (see "Motion" below). -->
    <div
      ref="track"
      class="gallery-track"
      :style="trackStyle"
      @scroll.passive="onTrackScroll"
    >
      <div
        v-for="(item, index) in items"
        :key="index"
        class="gallery-item"
        :style="{ background: bgColor }"
        :aria-hidden="index === activeIndex ? undefined : 'true'"
        :inert="index !== activeIndex || undefined"
      >
        <div class="gallery-item-inner">
          <!-- Video slide. Nothing of the clip is on the network until the
               visitor asks for it: the poster is a plain image, and the
               <video> element is not even created until the first press.
               The poster stays on top until the clip is playing, then
               `is-video-shown` hides it so the clip itself is visible. -->
          <div
            v-if="isVideoItem(item)"
            class="gallery-video-slide"
            :class="{ 'is-video-shown': isVideoShown(index) }"
          >
            <img
              v-if="index > 0 || canPreviewMedia(index)"
              :key="`preview-${slotGeneration(index)}-${
                mediaRetries[index] || 0
              }`"
              class="gallery-img gallery-media-preview"
              :class="{
                'is-loaded': previewPaintedMediaIndices.has(index),
                'is-retiring': paintedMediaIndices.has(index),
              }"
              :src="
                canPreviewMedia(index) ? previewSrc(index, item) : deferredSrc
              "
              alt=""
              loading="eager"
              decoding="async"
              fetchpriority="low"
              aria-hidden="true"
              draggable="false"
              @load="onPreviewLoad(index, $event)"
              @error="onPreviewError(index)"
            />
            <!-- The full-frame poster, never a square rendition: the clip
                 replaces it in place, so both must have the same framing. -->
            <img
              :key="`poster-${slotGeneration(index)}-${
                mediaRetries[index] || 0
              }`"
              class="gallery-img gallery-video-poster"
              :class="{
                'is-loaded': paintedMediaIndices.has(index),
                'is-ssr-hero': index === 0 && heroFromServer,
                'is-instant': index === 0 && heroInstant,
              }"
              :alt="item?.alt || ''"
              :loading="canLoadMedia(index) ? 'eager' : 'lazy'"
              decoding="async"
              :fetchpriority="index === priorityMediaIndex ? 'high' : 'low'"
              :src="canLoadMedia(index) ? posterSrc(index, item) : deferredSrc"
              draggable="false"
              @load="onMediaLoad(index, $event)"
              @error="onMediaError(index, item)"
              @click="onVideoToggle($event, index)"
            />

            <!-- No `poster` attribute: the <img> above covers the element
                 until it plays, and a second poster would be a second
                 download. -->
            <video
              v-if="isVideoActivated(index)"
              :ref="(el) => setVideoRef(index, el as HTMLVideoElement | null)"
              class="gallery-video"
              :src="item.videoUrl"
              muted
              playsinline
              loop
              preload="auto"
              disablepictureinpicture
              controlslist="nodownload noremoteplayback noplaybackrate"
              draggable="false"
              @playing="onVideoPlaying(index)"
              @waiting="onVideoWaiting(index)"
              @pause="onVideoPause(index)"
              @error="onVideoError(index)"
              @click="onVideoToggle($event, index)"
            >
              <!-- Muted product clip, no dialogue: empty captions track for WCAG 1.2.2 -->
              <track
                kind="captions"
                srclang="en"
                label="No dialogue"
                src="data:text/vtt,WEBVTT%0A%0A"
              />
            </video>

            <!-- Play affordance, and the pause target once running. Hidden by
                 class rather than v-if so it can cross-fade instead of
                 popping in and out under the visitor's finger. -->
            <button
              type="button"
              class="gallery-video-play"
              :class="{
                'is-hidden': isVideoPlaying(index) || isVideoBuffering(index),
              }"
              :aria-label="
                isVideoActivated(index) ? 'Play video' : 'Load and play video'
              "
              :tabindex="index === activeIndex ? 0 : -1"
              @click="onVideoToggle($event, index)"
            >
              <svg
                class="gallery-video-play__icon"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <path d="M8 5.5v13l11-6.5z" fill="currentColor" />
              </svg>
            </button>

            <span
              v-if="isVideoBuffering(index)"
              class="gallery-video-spinner"
              role="status"
              aria-label="Loading video"
            />
          </div>

          <template v-else>
            <!-- Shared-transition fallback. It uses the exact already-decoded
                 catalogue rendition and lives inside slide one, so the gallery
                 is immediately swipeable after hand-off even while the PDP
                 rendition is still decoding underneath. -->
            <img
              v-if="
                index === 0 && transitionPreviewSrc && transitionPreviewVisible
              "
              :src="transitionPreviewSrc"
              alt=""
              class="gallery-img gallery-transition-preview is-loaded"
              aria-hidden="true"
              decoding="sync"
              draggable="false"
            />

            <!-- Tiny blurred placeholder of a `media` photo (inline data URI) on
                 slides after the first, under every other layer until the
                 slide's photo or preview paints; it fades out then. Not on the
                 hero: the server-painted hero would show it through its
                 transparent pixels until hydration. Its own class keeps it out
                 of every `.gallery-img` / preview selector. -->
            <img
              v-if="slotLqip(index, item)"
              :src="slotLqip(index, item)"
              alt=""
              class="gallery-media-lqip"
              :class="{
                'is-retiring':
                  paintedMediaIndices.has(index) ||
                  previewPaintedMediaIndices.has(index),
              }"
              aria-hidden="true"
              draggable="false"
            />

            <img
              v-if="index > 0 || canPreviewMedia(index)"
              :key="`preview-${slotGeneration(index)}-${
                mediaRetries[index] || 0
              }`"
              :src="
                canPreviewMedia(index) ? previewSrc(index, item) : deferredSrc
              "
              alt=""
              class="gallery-img gallery-media-preview"
              :style="mediaZoomStyle(index)"
              :class="{
                'is-loaded': previewPaintedMediaIndices.has(index),
                'is-retiring': paintedMediaIndices.has(index),
              }"
              loading="eager"
              decoding="async"
              fetchpriority="low"
              aria-hidden="true"
              draggable="false"
              @load="onPreviewLoad(index, $event)"
              @error="onPreviewError(index)"
            />

            <!-- A photo with `media`: the AVIF ladder for browsers that decode
                 it, the JPEG pair (w640 + share) for the rest (SPEC §3). The
                 sources stay empty until the slot may load, like the legacy
                 <img> below. A failure falls back to that legacy <img>. -->
            <picture
              v-if="slotMedia(index, item)"
              :key="`photo-${slotGeneration(index)}-${
                mediaRetries[index] || 0
              }-media`"
              class="gallery-picture"
            >
              <source
                type="image/avif"
                :srcset="slotMediaAvifSrcset(index, item)"
                :sizes="slotMediaSizes(index)"
              />
              <img
                :alt="item?.alt || ''"
                class="gallery-img"
                :class="{
                  'is-loaded': paintedMediaIndices.has(index),
                  'is-ssr-hero': index === 0 && heroFromServer,
                  'is-instant': index === 0 && heroInstant,
                }"
                :style="mediaZoomStyle(index)"
                :loading="canLoadMedia(index) ? 'eager' : 'lazy'"
                decoding="async"
                :fetchpriority="index === priorityMediaIndex ? 'high' : 'low'"
                :sizes="slotMediaSizes(index)"
                :srcset="slotMediaJpegSrcset(index, item)"
                :src="slotMediaJpegSrc(index, item)"
                draggable="false"
                @load="onMediaLoad(index, $event)"
                @error="onMediaError(index, item)"
                @click="onImageTap($event, index, item)"
              />
            </picture>
            <img
              v-else
              :key="`photo-${slotGeneration(index)}-${
                mediaRetries[index] || 0
              }-${originalSourceIndices.has(index)}`"
              :alt="item?.alt || ''"
              class="gallery-img"
              :class="{
                'is-loaded': paintedMediaIndices.has(index),
                'is-ssr-hero': index === 0 && heroFromServer,
                'is-instant': index === 0 && heroInstant,
              }"
              :style="mediaZoomStyle(index)"
              :loading="canLoadMedia(index) ? 'eager' : 'lazy'"
              decoding="async"
              :fetchpriority="index === priorityMediaIndex ? 'high' : 'low'"
              :sizes="slotSizes(index, item)"
              :srcset="slotSrcset(index, item)"
              :src="canLoadMedia(index) ? resolveDisplaySrc(item) : deferredSrc"
              draggable="false"
              @load="onMediaLoad(index, $event)"
              @error="onMediaError(index, item)"
              @click="onImageTap($event, index, item)"
            />
          </template>
        </div>
      </div>
    </div>

    <div v-if="items.length > 1" class="gallery-counter" aria-live="polite">
      {{ activeIndex + 1 }}/{{ items.length }}
    </div>
    <p v-if="mediaError" class="gallery-load-message" role="status">
      {{ mediaError }}
    </p>
    <div
      v-else-if="
        failedMediaIndices.has(activeIndex) &&
        !previewPaintedMediaIndices.has(activeIndex)
      "
      class="gallery-load-message"
      role="status"
    >
      This photo couldn't load.
      <button type="button" @click.stop="retryMedia">Try again</button>
    </div>

    <div v-if="items.length > 1" class="gallery-pagination" aria-hidden="true">
      <span class="gallery-pagination-line">
        <span
          ref="progressEl"
          class="gallery-pagination-progress"
          :style="progressStyle"
        />
      </span>
    </div>
  </div>
</template>

<script lang="ts">
// Client only: the hero the server delivered, until one replacement gallery
// instance (a viewport-mode swap right after hydration) takes it over.
let serverHeroImage = "";
</script>

<script setup lang="ts">
import {
  ref,
  reactive,
  computed,
  onMounted,
  onBeforeUnmount,
  watch,
  nextTick,
} from "vue";
import { hasRenditions, productAvifSrcset, renditionUrl } from "~/utils/cdn";
import {
  productMediaPicture,
  productMediaRungSrc,
} from "~/core/utils/productMedia";
import {
  buildGalleryWarmOrder,
  galleryWarmLimit,
  GALLERY_SIZES,
  GALLERY_WARM_CONCURRENCY,
  type GalleryConnectionInfo,
} from "~/core/utils/galleryPreloadPolicy";
import { isHydrated } from "~/core/utils/hydration";

interface GalleryItem {
  type?: string;
  videoUrl?: string;
  image?: string;
  imageBig?: string;
  imageLazy?: string;
  src?: string;
  src2x?: string;
  alt?: string;
  // Video items only, tried in this order when the CDN poster derived from the
  // clip's URL cannot be shown: the JPEG still of the same frame, then the
  // product photo (see useProductMediaGallery).
  posterFrame?: string;
  posterFallback?: string;
  // Photos only: this slot's own Hooligans product-images renditions
  // (utils/productMedia), or null/absent for the legacy fields.
  media?: unknown;
}

const props = defineProps({
  items: {
    type: Array as () => GalleryItem[],
    default: () => [],
  },
  height: {
    type: String,
    default: "400px",
  },
  bgColor: {
    type: String,
    default: "#f5f5f5",
  },
  transitionPreviewSrc: {
    type: String,
    default: "",
  },
  thumbnailPreviews: {
    type: Object as () => Record<string, string>,
    default: () => ({}),
  },
});

const emit = defineEmits<{
  (e: "image-click", payload: { index: number; item: GalleryItem }): void;
  (e: "slide-change", payload: { index: number }): void;
}>();

const galleryRoot = ref<HTMLElement | null>(null);
const track = ref<HTMLElement | null>(null);
const progressEl = ref<HTMLElement | null>(null);
const activeIndex = ref(0);
const priorityMediaIndex = ref(0);
const dragging = ref(false);
const didDrag = ref(false);
const transitionPreviewVisible = ref(Boolean(props.transitionPreviewSrc));
// The hero replaced the hand-off photo: it appeared in one frame (see
// decodeVisibleImage), so it carries no opacity transition.
const heroInstant = ref(false);
const mediaError = ref("");

// --- Motion -------------------------------------------------------------------
// The track is a horizontal scroller with mandatory scroll snapping, one photo
// per swipe (`scroll-snap-stop: always`). On a touch screen it scrolls
// natively and the browser moves it: on iOS in the UI process at the display's
// own rate (120 Hz on ProMotion), however busy the main thread is. With a
// mouse only, its overflow is hidden as before (no trackpad or keyboard
// scrolling of its own) and script moves it. The previous engine (a non-passive touchmove writing a
// transform, then a rAF spring) ran entirely on the main thread: in the lab on
// 2026-10-15 a swipe that met the post-hydration work froze for 67-344 ms,
// and Safari runs rAF animations at 60 Hz (report
// shared/docs/reports/2026-10/PDP-GALLERY-SWIPE-2026-10-15.uk.md).
// Script only follows the scroll (counter, progress bar, warm-up, video) and
// starts the moves the browser cannot: keyboard, thumbnails and a desktop mouse
// drag, all through `scrollLeft` / `scrollTo`. Every listener on the gallery is
// passive, so nothing here can hold a touch scroll back.
let slideWidth = 1;
// A user scroll (touch, trackpad, mouse drag) is under way since its first
// scroll event; `gestureOrigin` is the slide it started from.
let scrollGesture = false;
let gestureOrigin = 0;
// A move the script started (goTo, a mouse drag's release, a resize): its
// destination is already the active slide, so its scroll events only paint.
let programmaticTarget: number | null = null;
let programmaticSince = 0;
let programmaticMoved = false;
// The settle that put the track back on its photo (see onScrollSettle).
let aligning = false;
// `scrollLeft` at the last quiet spell (see onSettleTimer); null after a
// scroll event.
let quietLeft: number | null = null;
// A finger is on the gallery: the scroll is not over until it lifts.
let touchActive = false;
let settleTimer: ReturnType<typeof setTimeout> | null = null;
// No scroll event for this long means the browser has finished the snap
// (`scrollend` settles at once where the browser has it).
const SETTLE_DELAY = 120;
// Snapping is switched off while a mouse drag writes `scrollLeft` (each write
// would snap) and until the release's smooth scroll has arrived.
let snapSuspended = false;

// --- Inline tap-to-zoom (Gucci-style) ---
// Tap magnifies the active photo in place at the tap point; while zoomed, a drag
// pans the image (instead of swiping the carousel); tapping again resets it.
const ZOOM_SCALE = 2.5; // magnification on tap (adjust to taste, e.g. 2.0-3.0)
const zoomedIndex = ref<number | null>(null);
const zoomTx = ref(0);
const zoomTy = ref(0);
const zoomPanning = ref(false);
let zoomMaxX = 0; // max pan offset that still keeps the frame covered
let zoomMaxY = 0;
let zoomStartTx = 0; // pan base captured at gesture start
let zoomStartTy = 0;
let panStartX = 0;
let panStartY = 0;

// Desktop mouse drag (touch and trackpads scroll natively).
let startX = 0;
let startY = 0;
let startScroll = 0;
let lastX = 0;
let lastTime = 0;
let velocity = 0;
let axis: "x" | "y" | null = null;

// Where a mouse drag lands: one slide at most, like a touch swipe.
const COMMIT_DISTANCE_RATIO = 0.2; // drag >20% of a slide to advance one
const FLICK_VELOCITY = 0.3; // px/ms; a faster release advances regardless of distance

const trackStyle = computed(() => ({
  "--gallery-height": props.height,
}));

// Width only; the offset is written in `paint`.
const progressStyle = computed(() => ({
  width: `${100 / Math.max(props.items.length, 1)}%`,
}));

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(value, max));

const lastIndex = () => Math.max(0, props.items.length - 1);
const scrollPosition = () => (track.value?.scrollLeft || 0) / slideWidth;

// The progress bar follows the scroll position; a transform write, no Vue.
// Callers that have just read the position pass it: reading `scrollLeft`
// after this write would make the browser recalculate style.
const paint = (position = scrollPosition()) => {
  const p = progressEl.value;
  if (p) {
    p.style.transform = `translateX(${clamp(position, 0, lastIndex()) * 100}%)`;
  }
};

const isVideoItem = (item?: GalleryItem) =>
  item?.type === "video" &&
  typeof item.videoUrl === "string" &&
  item.videoUrl.trim().length > 0;

// --- Responsive media -------------------------------------------------------
// Neighbours warm the same responsive rendition that selection will use.
// Desktop thumbnails also lend their already-loaded 600px source to any slide,
// including distant jumps, until its sharper rendition has decoded.
const resolveImageSrc = (item?: GalleryItem) =>
  item?.image || item?.imageBig || item?.src || "";
const resolveDisplaySrc = (item?: GalleryItem) => resolveImageSrc(item);

// The server-rendered hero is the LCP element: it paints as soon as its bytes
// arrive, without waiting for hydration to mark it painted. True in SSR and in
// the hydration render alike, so the class matches the server markup; it only
// fades when the client swaps the hero (catalogue hand-off, another product,
// a failed image). When the post-hydration viewport check swaps the desktop
// and phone galleries, the replacement keeps the same hero visible instead of
// hiding it again.
const heroImage = resolveImageSrc(props.items[0]);
const hydratingServerHero = !isHydrated();
const inheritsServerHero =
  !hydratingServerHero && !!heroImage && heroImage === serverHeroImage;
if (inheritsServerHero) serverHeroImage = "";
const heroFromServer = ref(
  (hydratingServerHero || inheritsServerHero) && !props.transitionPreviewSrc
);
if (
  typeof window !== "undefined" &&
  hydratingServerHero &&
  heroFromServer.value
)
  serverHeroImage = heroImage;

const measuredSizes = ref(GALLERY_SIZES);
const deferredSrc =
  "data:image/gif;base64,R0lGODlhAQABAAD/ACwAAAAAAQABAAACADs=";

const loadedMediaIndices = reactive(new Set<number>());
const visitedMediaIndices = new Set<number>();
const previewMediaIndices = reactive(new Set<number>());
const previewPaintedMediaIndices = reactive(new Set<number>());
const paintedMediaIndices = reactive(new Set<number>());
const failedMediaIndices = reactive(new Set<number>());
const originalSourceIndices = reactive(new Set<number>());
// Legacy photos (by URL) that already painted in this gallery keep their
// legacy source: a catalogue preview has no `media`, and its item list differs
// from the full answer's (the gallery resets on it), so swapping the shown
// hero for the full answer's renditions would only blink and download it
// again. Keyed by URL without its query (the API's `?v=` cache-buster can
// differ between the catalogue answer and the PDP answer for the same file),
// so the pin survives that reset and never applies to another photo. The slot
// still shows its own current legacy URL.
const legacyPaintedSources = reactive(new Set<string>());
const legacyPinKey = (item?: GalleryItem) =>
  resolveImageSrc(item).split(/[?#]/)[0];
const mediaRetries = reactive<Record<number, number>>({});
const pendingMediaIndices = new Set<number>();
const posterOverrides = reactive<Record<number, string>>({});
const previewOverrides = reactive<Record<number, string>>({});
const canLoadMedia = (index: number) => loadedMediaIndices.has(index);
const canPreviewMedia = (index: number) => previewMediaIndices.has(index);
let warmHandle: number | null = null;
let warmHandleIsIdle = false;
let warmGeneration = 0;
// Slide one keeps its own generation: the same product's full answer replaces
// a catalogue preview list without re-creating the hero shown (items watch).
let heroGeneration = 0;
const slotGeneration = (index: number) =>
  index === 0 ? heroGeneration : warmGeneration;
let heroReady = false;
let warmDirection = 1;

const slotSrcset = (index: number, item?: GalleryItem) =>
  canLoadMedia(index) && !originalSourceIndices.has(index)
    ? productAvifSrcset(resolveImageSrc(item)) || undefined
    : undefined;
const slotSizes = (index: number, item?: GalleryItem) =>
  slotSrcset(index, item) ? measuredSizes.value : undefined;
// The slot's `media` picture, or null: videos, slots that fell back to their
// original after an error, and slots already painted from legacy fields use
// the legacy <img>.
const slotMedia = (index: number, item?: GalleryItem) =>
  isVideoItem(item) ||
  originalSourceIndices.has(index) ||
  legacyPaintedSources.has(legacyPinKey(item))
    ? null
    : productMediaPicture(item?.media);
const slotMediaSizes = (index: number) =>
  canLoadMedia(index) ? measuredSizes.value : undefined;
const slotMediaAvifSrcset = (index: number, item?: GalleryItem) =>
  canLoadMedia(index) ? slotMedia(index, item)?.avifSrcset : undefined;
const slotMediaJpegSrcset = (index: number, item?: GalleryItem) =>
  canLoadMedia(index) ? slotMedia(index, item)?.jpegSrcset : undefined;
const slotMediaJpegSrc = (index: number, item?: GalleryItem) =>
  (canLoadMedia(index) && slotMedia(index, item)?.jpegSrc) || deferredSrc;
const slotLqip = (index: number, item?: GalleryItem) =>
  index === 0 || failedMediaIndices.has(index)
    ? ""
    : slotMedia(index, item)?.lqip || "";
const resolvePoster = (index: number, item?: GalleryItem) =>
  posterOverrides[index] || resolveImageSrc(item);
const posterSrc = (index: number, item?: GalleryItem) =>
  resolvePoster(index, item);
// A video poster that failed steps to the next source: the AVIF poster, then
// the JPEG still of the same frame (decoders that reject the AVIF; iPhones
// fell through to the product photo on 2026-10-01), then the product photo.
// Returns false once every source has been tried.
const advancePoster = (index: number, item?: GalleryItem) => {
  const current = resolvePoster(index, item);
  const sources = [item?.image, item?.posterFrame, item?.posterFallback];
  const next = sources
    .slice(sources.indexOf(current) + 1)
    .find((source) => source && source !== current);
  if (!next) return false;
  posterOverrides[index] = next;
  return true;
};
const previewSrc = (index: number, item?: GalleryItem) => {
  if (previewOverrides[index]) return previewOverrides[index];
  // A `media` slot previews its own rung nearest the legacy 600px preview.
  const mediaPreview = slotMedia(index, item)
    ? productMediaRungSrc(item?.media, 600)
    : "";
  if (mediaPreview) return mediaPreview;
  const source = isVideoItem(item)
    ? resolvePoster(index, item)
    : resolveImageSrc(item);
  return hasRenditions(source)
    ? renditionUrl(source, 600, { ratio: 1, format: "avif" }) || source
    : source;
};
const hasResponsiveRendition = (index: number) => {
  const item = props.items[index];
  // The API currently fabricates lazy_*.webp URLs without checking that the
  // files exist. The production catalogue has no such files, so imageLazy is
  // not a valid preview signal. Only warm an image when a real CDN rendition
  // can be derived from the canonical image URL, or the slot has `media`.
  return !!slotMedia(index, item) || hasRenditions(resolveImageSrc(item));
};

// Only a finger or a mouse on the gallery (swipe, drag or zoom pan) holds
// speculation back; no glide does (release snap, keyboard or thumbnail). A
// brisk swiper starts the next swipe before the previous one has settled, so
// waiting for it meant every photo from the fourth on started loading at the
// swipe's start. Fetch and decode run off the main thread, and the scroll does
// not wait for the re-renders a warmed photo causes (src set, then is-loaded).
const isGesturing = () => dragging.value || zoomPanning.value || touchActive;
const browserConnection = () => {
  if (typeof navigator === "undefined") return null;
  return (navigator as Navigator & { connection?: GalleryConnectionInfo })
    .connection;
};
const cancelWarmSchedule = () => {
  if (warmHandle === null || typeof window === "undefined") return;
  if (warmHandleIsIdle) (window as any).cancelIdleCallback?.(warmHandle);
  else window.clearTimeout(warmHandle);
  warmHandle = null;
};

// Abort obsolete, unfinished speculation by withdrawing its source. Already
// painted previews and explicitly visited photos remain available for backswipes.
const pruneWarmMedia = (keep: Set<number>) => {
  for (const index of pendingMediaIndices) {
    if (keep.has(index) || visitedMediaIndices.has(index)) continue;
    loadedMediaIndices.delete(index);
    pendingMediaIndices.delete(index);
  }
};
const flushWarmQueue = () => {
  warmHandle = null;
  if (!heroReady || document.hidden || !isGalleryOnScreen.value) return;
  if (isGesturing()) return; // the release (settle) schedules the next window
  const centre = priorityMediaIndex.value;
  // The selected image always gets bandwidth before speculative neighbours.
  if (
    !paintedMediaIndices.has(centre) &&
    !previewPaintedMediaIndices.has(centre) &&
    !failedMediaIndices.has(centre)
  )
    return;
  const limit = galleryWarmLimit(props.items.length, browserConnection());
  const order = buildGalleryWarmOrder(
    props.items.length,
    centre,
    limit,
    warmDirection
  );
  pruneWarmMedia(new Set(order));
  for (const index of order) {
    if (pendingMediaIndices.size >= Math.min(limit, GALLERY_WARM_CONCURRENCY))
      break;
    if (
      canLoadMedia(index) ||
      failedMediaIndices.has(index) ||
      !hasResponsiveRendition(index)
    )
      continue;
    // Warm the final element itself: selection never creates another request
    // or decode for the same rendition, even with the HTTP cache disabled.
    pendingMediaIndices.add(index);
    loadedMediaIndices.add(index);
  }
};
function scheduleWarm() {
  if (typeof window === "undefined" || warmHandle !== null) return;
  if (document.hidden || !isGalleryOnScreen.value) return;
  const ric = (window as any).requestIdleCallback;
  if (typeof ric === "function") {
    warmHandleIsIdle = true;
    warmHandle = ric(flushWarmQueue, { timeout: 400 });
  } else {
    warmHandleIsIdle = false;
    warmHandle = window.setTimeout(flushWarmQueue, 120);
  }
}
// keepWarmWindow: a swipe to the next slide keeps warming the neighbours that
// stay inside the warm window around it (dragging to k+1 must not abort k+2,
// which the shopper is about to reach); jumps still drop all speculation.
const prioritizeMedia = (index: number, keepWarmWindow = false) => {
  const target = clamp(index, 0, Math.max(0, props.items.length - 1));
  if (target !== priorityMediaIndex.value)
    warmDirection = target > priorityMediaIndex.value ? 1 : -1;
  priorityMediaIndex.value = target;
  // Release stale speculation at the first sign of swipe intent.
  cancelWarmSchedule();
  visitedMediaIndices.add(target);
  const keep = new Set([target]);
  if (keepWarmWindow) {
    const limit = galleryWarmLimit(props.items.length, browserConnection());
    for (const neighbour of buildGalleryWarmOrder(
      props.items.length,
      target,
      limit,
      warmDirection
    ))
      keep.add(neighbour);
  }
  pruneWarmMedia(keep);
  pendingMediaIndices.delete(target);
  loadedMediaIndices.add(target);
  // An existing preview stays underneath until the selected rendition decodes.
  // A cold jump starts only the requested rendition, never a second download.
};

const decodeVisibleImage = async (
  index: number,
  image: HTMLImageElement | null,
  preview = false
) => {
  if (!image || !image.complete || image.naturalWidth <= 1) return;
  const source = image.currentSrc || image.src;
  if (!source || source === deferredSrc) return;
  const generation = slotGeneration(index);
  try {
    if (typeof image.decode === "function") await image.decode();
  } catch {
    // Some engines reject decode after a successful load. Reveal only if the
    // same connected element still owns a complete, usable image.
  }
  if (
    generation !== slotGeneration(index) ||
    !image.isConnected ||
    source !== (image.currentSrc || image.src) ||
    !image.complete ||
    image.naturalWidth <= 1
  )
    return;
  if (preview) {
    previewPaintedMediaIndices.add(index);
  } else {
    pendingMediaIndices.delete(index);
    paintedMediaIndices.add(index);
    failedMediaIndices.delete(index);
    const painted = props.items[index];
    if (!isVideoItem(painted) && !slotMedia(index, painted)) {
      const key = legacyPinKey(painted);
      if (key) legacyPaintedSources.add(key);
    }
    if (index === 0) {
      heroReady = true;
      // Swap the decoded hero for the hand-off photo in one render: the hero
      // shows at full opacity as the photo under it goes (.is-instant).
      if (transitionPreviewVisible.value) {
        heroInstant.value = true;
        transitionPreviewVisible.value = false;
      }
    }
  }
  scheduleWarm();
};
const onMediaLoad = (index: number, event?: Event) => {
  if (!canLoadMedia(index)) return;
  const image =
    (event?.currentTarget as HTMLImageElement | null) ||
    galleryRoot.value
      ?.querySelectorAll(".gallery-item")
      [index]?.querySelector<HTMLImageElement>(
        ".gallery-img:not(.gallery-media-preview):not(.gallery-transition-preview)"
      ) ||
    null;
  void decodeVisibleImage(index, image);
};
const onPreviewLoad = (index: number, event: Event) => {
  if (!canPreviewMedia(index)) return;
  void decodeVisibleImage(index, event.currentTarget as HTMLImageElement, true);
};
const onMediaError = (index: number, item?: GalleryItem) => {
  if (!canLoadMedia(index)) return;
  // A broken hero must not stay visible; its fallback fades in like any slide.
  if (index === 0) {
    heroFromServer.value = false;
    serverHeroImage = "";
  }
  if (isVideoItem(item)) {
    // A poster has no responsive renditions to drop: try its next source.
    if (advancePoster(index, item)) return;
  } else if (
    // Missing responsive rendition (or `media` rung): try the existing
    // original once. Never guess alternative CDN names and never loop if
    // that original is also unavailable.
    (slotMedia(index, item) || hasRenditions(resolveImageSrc(item))) &&
    !originalSourceIndices.has(index)
  ) {
    originalSourceIndices.add(index);
    return;
  }
  paintedMediaIndices.delete(index);
  pendingMediaIndices.delete(index);
  failedMediaIndices.add(index);
  if (index === 0) heroReady = true;
  scheduleWarm();
};
// A failed preview only loses the preview. The poster underneath has its own
// error handler, so a broken borrowed thumbnail never moves the poster on.
const onPreviewError = (index: number) => {
  previewPaintedMediaIndices.delete(index);
  scheduleWarm();
};
const retryMedia = () => {
  const index = activeIndex.value;
  if (!failedMediaIndices.has(index)) return;
  failedMediaIndices.delete(index);
  originalSourceIndices.delete(index);
  // A video poster starts again from its own AVIF.
  delete posterOverrides[index];
  mediaRetries[index] = (mediaRetries[index] || 0) + 1;
  prioritizeMedia(index);
};
const onGalleryVisibilityChange = () => {
  if (document.hidden) {
    pauseActiveVideo();
    cancelWarmSchedule();
    pruneWarmMedia(new Set());
  } else {
    retryMedia();
    scheduleWarm();
  }
};

const videoRefs = new Map<number, HTMLVideoElement>();

// --- Play on request --------------------------------------------------------
// A clip is downloaded ONLY after the visitor presses play. Until then the slide
// is a poster image plus a play button, and no <video> element exists for it at
// all — so there is no `src`, no `preload`, no metadata request, nothing on the
// network. Simply reaching the slide is not consent to spend someone's data.
//
// (Previously the clip auto-loaded and auto-played as soon as its slide settled,
// which on a phone meant a multi-megabyte download and a decode the visitor had
// not asked for, every time they swiped past it.)
//
// Once activated, a clip stays mounted and cached, so pausing/resuming and
// swiping back to it are instant.
const activatedVideos = reactive(new Set<number>());
const playingVideos = reactive(new Set<number>());
const bufferingVideos = reactive(new Set<number>());
// Clips that have started playing: their poster is hidden so the clip itself
// shows, and stays hidden through pauses (the paused frame is what the
// visitor expects to see). Until then the poster covers the element, so a
// clip that never starts, or fails, leaves the poster in place.
const shownVideos = reactive(new Set<number>());

// Pauses WE issue (swipe, offscreen, slide change) as opposed to the visitor
// pressing pause. The distinction matters for the overlay: a transient pause
// during a swipe must not flash the play button back over a clip we are about
// to resume ourselves.
const autoPausedVideos = new Set<number>();

const isVideoActivated = (index: number) => activatedVideos.has(index);
const isVideoPlaying = (index: number) => playingVideos.has(index);
const isVideoBuffering = (index: number) => bufferingVideos.has(index);
const isVideoShown = (index: number) => shownVideos.has(index);

// Pause whatever video is playing. Used while swiping so video decode work doesn't
// compete with the carousel's animation frames.
const pauseActiveVideo = () => {
  videoRefs.forEach((el, idx) => {
    try {
      autoPausedVideos.add(idx);
      el.pause();
    } catch {
      // ignore
    }
  });
};

const setVideoRef = (index: number, el: HTMLVideoElement | null) => {
  if (el) {
    videoRefs.set(index, el);
  } else {
    videoRefs.delete(index);
  }
};

// Swiping to another slide only pauses — the clip keeps its position so coming
// back resumes where the visitor left off rather than restarting.
const pauseOtherVideos = (excludeIndex?: number) => {
  videoRefs.forEach((el, idx) => {
    if (excludeIndex !== undefined && idx === excludeIndex) return;
    try {
      autoPausedVideos.add(idx);
      el.pause();
    } catch {
      // ignore
    }
  });
};

// Full reset, used when the gallery switches to a different product.
const resetAllVideos = () => {
  videoRefs.forEach((el) => {
    try {
      el.pause();
      el.currentTime = 0;
    } catch {
      // ignore
    }
  });
  activatedVideos.clear();
  playingVideos.clear();
  bufferingVideos.clear();
  shownVideos.clear();
  autoPausedVideos.clear();
};

const playVideoAtIndex = (index: number) => {
  const el = videoRefs.get(index);
  if (!el) return;
  try {
    el.muted = true;
    const p = el.play();
    if (p && typeof p.catch === "function") {
      // Rejected play (autoplay policy, decode failure): drop the spinner and
      // put the play button back so the visitor can try again.
      p.catch(() => {
        bufferingVideos.delete(index);
        playingVideos.delete(index);
      });
    }
  } catch {
    bufferingVideos.delete(index);
    playingVideos.delete(index);
  }
};

// Slide changed: pause everything else, and resume this slide's clip ONLY if
// the visitor had already started it. Reaching a video slide never starts it.
const applyVideoPlaybackForActiveIndex = () => {
  const idx = activeIndex.value;
  pauseOtherVideos(idx);
  // Never (re)start playback while the gallery is scrolled offscreen — the
  // visibility observer resumes the active video when it comes back.
  if (!isGalleryOnScreen.value) return;
  if (!activatedVideos.has(idx)) return;
  const item = props.items[idx];
  if (!item || !isVideoItem(item)) return;
  void nextTick(() => {
    playVideoAtIndex(idx);
  });
};

// --- Video element events ---------------------------------------------------
const onVideoPlaying = (index: number) => {
  autoPausedVideos.delete(index);
  bufferingVideos.delete(index);
  playingVideos.add(index);
  shownVideos.add(index);
};

const onVideoWaiting = (index: number) => {
  if (playingVideos.has(index)) return; // mid-playback stall: don't flash the spinner
  bufferingVideos.add(index);
};

const onVideoPause = (index: number) => {
  bufferingVideos.delete(index);
  // A pause we issued ourselves is transient — keep the overlay hidden.
  if (autoPausedVideos.has(index)) return;
  playingVideos.delete(index);
};

// Unplayable clip (bad URL, unsupported codec): fall all the way back to the
// poster + play button rather than leaving a dead black rectangle.
const onVideoError = (index: number) => {
  bufferingVideos.delete(index);
  playingVideos.delete(index);
  shownVideos.delete(index);
  activatedVideos.delete(index);
};

// The single entry point for the play button and for tapping the clip itself.
const onVideoToggle = (event: MouseEvent, index: number) => {
  event.stopPropagation();
  // A swipe that happens to end on the button is a swipe, not a tap.
  if (didDrag.value) return;

  if (!activatedVideos.has(index)) {
    // First press: this is the moment the clip is allowed on the network.
    activatedVideos.add(index);
    bufferingVideos.add(index);
    void nextTick(() => playVideoAtIndex(index));
    return;
  }

  const el = videoRefs.get(index);
  if (!el) return;

  if (el.paused) {
    autoPausedVideos.delete(index);
    playVideoAtIndex(index);
  } else {
    autoPausedVideos.delete(index); // an explicit pause must show the overlay
    el.pause();
  }
};

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const settledIndex = () => clamp(Math.round(scrollPosition()), 0, lastIndex());

// Snapping off (a mouse drag writes `scrollLeft`) or back on. Written straight
// to the node: a class would only apply after the next render, and the first
// writes of the drag would snap back meanwhile.
const suspendSnap = (suspend: boolean) => {
  snapSuspended = suspend;
  const t = track.value;
  if (t) t.style.scrollSnapType = suspend ? "none" : "";
};

const scheduleSettle = (keepQuiet = false) => {
  if (!keepQuiet) quietLeft = null;
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = setTimeout(onSettleTimer, SETTLE_DELAY);
};

// No scroll event for SETTLE_DELAY is not yet the end of a scroll: a busy main
// thread can hold scroll events back while the browser is still gliding, and
// settling then would read a stale position and could stop the glide
// (independent review 2026-10-15, P2-1). The scroll settles once its position
// has also stayed put for another SETTLE_DELAY; `scrollend` settles at once.
function onSettleTimer() {
  settleTimer = null;
  const left = track.value?.scrollLeft || 0;
  if (quietLeft === null || Math.abs(left - quietLeft) > 0.5) {
    quietLeft = left;
    scheduleSettle(true);
    return;
  }
  onScrollSettle();
}

// The release of a swipe or a mouse drag. It moves one slide at most (or
// snaps back): keep the neighbours that stay in the window, as at the start of
// the gesture, so the release does not abort the k+2 warm-up the gesture just
// kept, and open the next window now, while the snap glides. No need to wait
// for idle time: the browser moves the track, and a snap can be over in
// 200 ms (WebKit).
const releaseTo = (targetIndex: number) => {
  prioritizeMedia(targetIndex, true);
  cancelWarmSchedule();
  if (typeof document !== "undefined") flushWarmQueue();
};

// A move the script starts: the destination is the active slide at once. It
// glides unless the visitor prefers reduced motion.
const scrollToIndex = (index: number) => {
  const target = clamp(index, 0, lastIndex());
  activeIndex.value = target;
  const t = track.value;
  if (!t) return;
  programmaticTarget = target;
  programmaticSince = Date.now();
  programmaticMoved = false;
  const left = target * slideWidth;
  // Always scroll, even when the track still reads as being there: a new
  // scroll aborts a glide that has just started elsewhere (goTo(2), goTo(1)).
  if (!prefersReducedMotion() && typeof t.scrollTo === "function")
    t.scrollTo({ left, behavior: "smooth" });
  else t.scrollLeft = left;
  scheduleSettle();
};

const onTrackScroll = () => {
  const position = scrollPosition();
  paint(position);
  if (programmaticTarget !== null) programmaticMoved = true;
  else {
    // First movement of a swipe, a trackpad scroll or a mouse drag: pause video
    // so its decode and network work stays out of the way, and start the photo
    // it heads for. The active clip resumes once the scroll settles.
    if (!scrollGesture) {
      scrollGesture = true;
      mediaError.value = "";
      pauseActiveVideo();
      const origin = activeIndex.value;
      gestureOrigin = origin;
      const next = clamp(origin + (position > origin ? 1 : -1), 0, lastIndex());
      if (next !== origin) prioritizeMedia(next, true);
    }
    activeIndex.value = clamp(Math.round(position), 0, lastIndex());
  }
  scheduleSettle();
};

// The scroll has come to rest (`scrollend`, or a quiet, unmoving spell; see
// onSettleTimer). The browser decided where a swipe ends: a snap back, or a
// release predicted wrongly, re-centres the window on where it landed.
function onScrollSettle() {
  if (settleTimer) clearTimeout(settleTimer);
  settleTimer = null;
  quietLeft = null;
  // A finger still down or a mouse drag in progress: their release settles.
  if (touchActive || dragging.value) return;
  // A glide the script started that has not begun yet (its first frame can
  // come late on a busy page) settles when it has moved, or after 1.5 s.
  if (
    programmaticTarget !== null &&
    !programmaticMoved &&
    Math.abs((track.value?.scrollLeft || 0) - programmaticTarget * slideWidth) >
      1 &&
    Date.now() - programmaticSince < 1500
  ) {
    scheduleSettle();
    return;
  }
  const landed = settledIndex();
  const wasGesture = scrollGesture;
  scrollGesture = false;
  programmaticTarget = null;
  if (snapSuspended) suspendSnap(false);
  if (wasGesture && landed !== priorityMediaIndex.value) releaseTo(landed);
  activeIndex.value = landed;
  // A glide cut short by another can stop a pixel or two off its photo
  // (Chrome, touch scroller: goTo(2) then goTo(1) ended at 392 of 390, and
  // nothing snapped it; its `scrollend` even came before the last scroll
  // event). Put it on the photo once; that scroll is the script's. Settling
  // waits for an unmoving position (onSettleTimer), so this never cuts into a
  // glide the browser is still running.
  const t = track.value;
  const left = landed * slideWidth;
  if (t && !aligning && Math.abs(t.scrollLeft - left) > 0.5) {
    aligning = true;
    programmaticTarget = landed;
    programmaticSince = Date.now();
    programmaticMoved = false;
    t.scrollLeft = left;
    scheduleSettle();
    return;
  }
  aligning = false;
  scheduleWarm();
  void nextTick(() => {
    applyVideoPlaybackForActiveIndex();
  });
}

const measure = () => {
  // SSR-safe: the items watcher runs with immediate:true during server render,
  // where there is no DOM/window. Fall back to a width of 1 there; the real
  // measurement happens on the client (onMounted + items watcher re-run).
  const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 0;
  // CSS slides can have fractional widths. clientWidth rounds them and the
  // error accumulates on later slides, exposing a strip of the next photo.
  // Computed width is unaffected by the PDP's entrance/zoom transforms.
  const t = track.value;
  const width =
    (t && parseFloat(getComputedStyle(t).width)) || viewportWidth || 1;
  const resized = slideWidth > 1 && Math.abs(width - slideWidth) > 0.5;
  slideWidth = width;
  if (t) measuredSizes.value = `${Math.ceil(width)}px`;
  // Steps of the scroll-driven progress bar (see the stylesheet). A minimal
  // DOM (unit-test VMs) has no setProperty: the bar then simply keeps the
  // script-written position.
  const bar = progressEl.value;
  if (typeof bar?.style?.setProperty === "function")
    bar.style.setProperty("--gallery-steps", String(lastIndex()));
  // A new width moves every snap point: keep the same photo in view.
  if (t && resized && !scrollGesture && !touchActive && !dragging.value) {
    programmaticTarget = activeIndex.value;
    programmaticSince = Date.now();
    programmaticMoved = false;
    t.scrollLeft = activeIndex.value * width;
    scheduleSettle();
  }
  paint();
};

// Decide where a mouse drag lands and glide there. One drag moves at most one
// slide: either it went past the threshold, or it was flicked.
const settle = () => {
  const originIndex = clamp(
    Math.round(startScroll / slideWidth),
    0,
    lastIndex()
  );
  const movedFromStart = (track.value?.scrollLeft || 0) - startScroll;
  const speed = Math.abs(velocity);

  // Direction: photo pulled left (scroll grows) -> next slide (+1).
  const direction =
    movedFromStart > 0
      ? 1
      : movedFromStart < 0
      ? -1
      : velocity < 0
      ? 1
      : velocity > 0
      ? -1
      : 0;

  const draggedFarEnough =
    Math.abs(movedFromStart) > COMMIT_DISTANCE_RATIO * slideWidth;
  const flicked = speed > FLICK_VELOCITY;

  const targetIndex =
    direction !== 0 && (draggedFarEnough || flicked)
      ? clamp(originIndex + direction, 0, lastIndex())
      : originIndex;

  scrollToIndex(targetIndex);
  releaseTo(targetIndex);
};

// Mouse drag (desktop). Touch screens and trackpads scroll natively.
const beginDrag = (x: number, y: number) => {
  // While zoomed, the same gesture pans the image instead of swiping slides.
  if (zoomedIndex.value !== null) {
    beginPan(x, y);
    return;
  }
  programmaticTarget = null;
  mediaError.value = "";
  dragging.value = true;
  didDrag.value = false;
  axis = null;
  startX = x;
  startY = y;
  lastX = x;
  lastTime = window.performance.now();
  startScroll = track.value?.scrollLeft || 0;
  velocity = 0;
};

const moveDrag = (x: number, y: number) => {
  if (zoomedIndex.value !== null) {
    movePan(x, y);
    return;
  }
  if (!dragging.value) return;

  const dx = x - startX;
  const dy = y - startY;

  if (!axis && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
    axis = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
  }

  if (axis !== "x") return;
  if (!snapSuspended) suspendSnap(true);
  didDrag.value = true;

  const now = window.performance.now();
  const dt = Math.max(now - lastTime, 1);
  velocity = (x - lastX) / dt;
  lastX = x;
  lastTime = now;

  const t = track.value;
  if (t) t.scrollLeft = startScroll - dx;
};

const onPointerUp = () => {
  // End a zoom-pan gesture (no carousel settle while zoomed).
  if (zoomPanning.value) {
    zoomPanning.value = false;
    window.removeEventListener("mousemove", onMouseMove);
    window.removeEventListener("mouseup", onPointerUp);
    // A warm that came due mid-pan was dropped; resume it.
    scheduleWarm();
    return;
  }

  if (!dragging.value) return;

  dragging.value = false;

  if (axis === "x") {
    settle();
  } else {
    // A click or a vertical drag: nothing will glide, so open the warm
    // window now.
    if (snapSuspended) suspendSnap(false);
    scheduleWarm();
  }

  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onPointerUp);
};

// Touch: the browser scrolls the track; these passive listeners only keep
// count of the finger, and pan a zoomed photo.
const onTouchStart = (event: TouchEvent) => {
  const touch = event.touches[0];
  if (zoomedIndex.value !== null) {
    beginPan(touch.clientX, touch.clientY);
    return;
  }
  // A finger takes over any glide the script started.
  touchActive = true;
  programmaticTarget = null;
  didDrag.value = false;
};

const onTouchMove = (event: TouchEvent) => {
  if (!zoomPanning.value) return;
  const touch = event.touches[0];
  movePan(touch.clientX, touch.clientY);
};

const onTouchEnd = (event: TouchEvent) => {
  if (zoomPanning.value) {
    onPointerUp();
    return;
  }
  if (event.touches.length) return; // another finger is still down
  touchActive = false;
  if (scrollGesture) {
    // Lifted mid-swipe: the snap moves one photo at most, the way the photo
    // was pulled (settling corrects a snap back).
    const position = scrollPosition();
    releaseTo(
      Math.abs(position - gestureOrigin) < 0.02
        ? gestureOrigin
        : clamp(
            gestureOrigin + (position > gestureOrigin ? 1 : -1),
            0,
            lastIndex()
          )
    );
  } else {
    // A tap: no scroll will settle, so open the warm window now.
    scheduleWarm();
  }
  scheduleSettle();
};

const onMouseDown = (event: MouseEvent) => {
  // A pointer interaction should leave the gallery ready for its documented
  // keyboard controls. Do not prevent the native event here: that would keep
  // the video play button from receiving its normal focus and activation.
  galleryRoot.value?.focus({ preventScroll: true });
  beginDrag(event.clientX, event.clientY);
  window.addEventListener("mousemove", onMouseMove);
  window.addEventListener("mouseup", onPointerUp);
};

const onMouseMove = (event: MouseEvent) => {
  moveDrag(event.clientX, event.clientY);
};

const clampZoom = () => {
  zoomTx.value = clamp(zoomTx.value, -zoomMaxX, zoomMaxX);
  zoomTy.value = clamp(zoomTy.value, -zoomMaxY, zoomMaxY);
};

// Per-slide zoom transform; identity (no inline style) for non-zoomed slides.
// transform-origin is the media centre (set in CSS), so the focal translate below
// keeps the tapped point under the finger.
const mediaZoomStyle = (index: number) => {
  if (zoomedIndex.value !== index) return {};
  return {
    transform: `translate3d(${zoomTx.value}px, ${zoomTy.value}px, 0) scale(${ZOOM_SCALE})`,
  };
};

const resetZoom = () => {
  zoomedIndex.value = null;
  zoomTx.value = 0;
  zoomTy.value = 0;
  zoomPanning.value = false;
};

// Tap toggles zoom. A tap that followed a drag (didDrag) is ignored, so swipes
// and pans never trigger a zoom.
const onImageTap = (event: MouseEvent, index: number, item: GalleryItem) => {
  if (didDrag.value) return;

  toggleImageZoom(
    index,
    item,
    event.currentTarget as HTMLElement | null,
    event.clientX,
    event.clientY
  );
};

// Both pointer and keyboard zoom use the same image-box calculation. Keyboard
// activation supplies the centre of the real rendered image, which preserves
// the existing zoom behaviour without inventing a second transform path.
const toggleImageZoom = (
  index: number,
  item: GalleryItem,
  el: HTMLElement | null,
  clientX: number,
  clientY: number
) => {
  // Second tap on the already-zoomed photo -> reset.
  if (zoomedIndex.value === index) {
    resetZoom();
    return;
  }

  if (!el) return;
  const rect = el.getBoundingClientRect();
  const s = ZOOM_SCALE;
  const w = rect.width || 1;
  const h = rect.height || 1;
  const px = clientX - rect.left; // tap point within the media box
  const py = clientY - rect.top;

  // How far the scaled media may pan while still covering the frame.
  zoomMaxX = ((s - 1) * w) / 2;
  zoomMaxY = ((s - 1) * h) / 2;

  // Translate so the tapped point stays put when scaling about the centre.
  zoomTx.value = (1 - s) * (px - w / 2);
  zoomTy.value = (1 - s) * (py - h / 2);
  clampZoom();

  zoomedIndex.value = index;
  emit("image-click", { index, item });
};

// Arrow keys pressed on a control inside the gallery (the video play button)
// move to a slide that makes the control's own slide inert. Hand focus to the
// gallery region first: otherwise the browser drops focus to <body> and the
// next arrow key no longer reaches the gallery.
const goToFromKeyboard = (index: number) => {
  const target = clamp(index, 0, Math.max(0, props.items.length - 1));
  const root = galleryRoot.value;
  const focused = document.activeElement;
  if (
    target !== activeIndex.value &&
    root &&
    focused !== root &&
    root.contains(focused)
  ) {
    root.focus({ preventScroll: true });
  }
  goTo(target);
};

const onGalleryKeydown = (event: KeyboardEvent) => {
  if (event.defaultPrevented) return;

  const target = event.target as Element | null;
  // A focused <video> owns its keys (seek, volume, play/pause) whenever it
  // exposes native controls.
  if (target?.closest("video")) return;

  if (event.key === "ArrowLeft" || event.key === "ArrowRight") {
    // Modified arrows belong to the browser, the OS and assistive tech: history
    // back / forward, word or line jumps, selection. Only plain arrows drive
    // the gallery.
    if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey)
      return;
    event.preventDefault();
    goToFromKeyboard(activeIndex.value + (event.key === "ArrowLeft" ? -1 : 1));
    return;
  }

  if (event.key === "Escape") {
    if (zoomedIndex.value === null) return;
    event.preventDefault();
    resetZoom();
    return;
  }

  if (event.key !== "Enter" && event.key !== " " && event.key !== "Spacebar") {
    return;
  }

  // A button keeps its native Enter / Space activation: the video play button
  // must play or pause the clip, never zoom a photo.
  if (target?.closest("button")) return;

  const index = activeIndex.value;
  const item = props.items[index];
  if (!item || isVideoItem(item)) return;

  const image = galleryRoot.value
    ?.querySelectorAll<HTMLElement>(".gallery-item")
    [index]?.querySelector<HTMLElement>(
      ".gallery-img:not(.gallery-video-poster):not(.gallery-media-preview):not(.gallery-transition-preview)"
    );
  if (!image) return;

  event.preventDefault();
  const rect = image.getBoundingClientRect();
  toggleImageZoom(
    index,
    item,
    image,
    rect.left + rect.width / 2,
    rect.top + rect.height / 2
  );
};

// Pan the zoomed media. Used in place of carousel swipe while zoomed.
const beginPan = (x: number, y: number) => {
  zoomPanning.value = true;
  didDrag.value = false;
  axis = null;
  panStartX = x;
  panStartY = y;
  zoomStartTx = zoomTx.value;
  zoomStartTy = zoomTy.value;
};

const movePan = (x: number, y: number) => {
  if (!zoomPanning.value) return;
  const dx = x - panStartX;
  const dy = y - panStartY;
  if (Math.abs(dx) > 4 || Math.abs(dy) > 4) didDrag.value = true;
  zoomTx.value = zoomStartTx + dx;
  zoomTy.value = zoomStartTy + dy;
  clampZoom();
};

// While a photo is zoomed a drag pans it, and the page must not scroll under
// the finger. Only then does the gallery hold a touch listener that can cancel
// touches: at any other time such a listener would make the browser wait for
// the main thread before it scrolls the track.
const preventPanScroll = (event: TouchEvent) => {
  if (zoomPanning.value && event.cancelable) event.preventDefault();
};
watch(zoomedIndex, (index) => {
  const root = galleryRoot.value;
  if (!root) return;
  if (index === null) root.removeEventListener("touchmove", preventPanScroll);
  else root.addEventListener("touchmove", preventPanScroll, { passive: false });
});

// Pause the looping product video whenever the gallery is scrolled out of
// view, resume the active slide's video when it returns. A muted looping
// clip kept playing behind the page while the user read the accordions
// below: every loop restart (~1s for short clips) forced a compositor
// commit that visibly nudged the ENTIRE page ±1px — measured frame-by-frame
// from a screen recording as rhythmic one-frame excursions at ~1Hz, and the
// reason only "some products" (the ones whose gallery has a video) ever
// wobbled. Pausing offscreen also saves decode work and battery.
let galleryVisibilityObserver: IntersectionObserver | null = null;
let galleryResizeObserver: ResizeObserver | null = null;
const isGalleryOnScreen = ref(true);

onMounted(() => {
  measure();
  // A swipe made before the page hydrated has already moved the scroller.
  const landed = settledIndex();
  if (landed !== activeIndex.value) activeIndex.value = landed;
  track.value?.addEventListener("scrollend", onScrollSettle);
  window.addEventListener("resize", measure);
  if (typeof ResizeObserver !== "undefined" && track.value) {
    galleryResizeObserver = new ResizeObserver(([entry]) => {
      if (Math.abs(entry.contentRect.width - slideWidth) > 0.01) measure();
    });
    galleryResizeObserver.observe(track.value);
  }
  window.addEventListener("online", retryMedia);
  document.addEventListener("visibilitychange", onGalleryVisibilityChange);
  // SSR may finish a cached hero before Vue attaches its load listener.
  const hero = galleryRoot.value?.querySelector<HTMLImageElement>(
    ".gallery-item:first-child .gallery-img:not(.gallery-transition-preview):not(.gallery-media-preview)"
  );
  if (hero?.complete && hero.naturalWidth > 1) onMediaLoad(0);
  // ...or fail before it: the missed error event must still hide it.
  else if (hero?.complete && hero.naturalWidth === 0 && heroFromServer.value)
    onMediaError(0, props.items[0]);

  if (
    typeof window !== "undefined" &&
    "IntersectionObserver" in window &&
    galleryRoot.value
  ) {
    galleryVisibilityObserver = new IntersectionObserver(
      (entries) => {
        const visible = !!entries[0]?.isIntersecting;
        if (visible === isGalleryOnScreen.value) return;
        isGalleryOnScreen.value = visible;
        if (visible) {
          applyVideoPlaybackForActiveIndex();
          scheduleWarm();
        } else {
          pauseActiveVideo();
          cancelWarmSchedule();
          pruneWarmMedia(new Set());
        }
      },
      { threshold: 0.01 }
    );
    galleryVisibilityObserver.observe(galleryRoot.value);
  }
});

onBeforeUnmount(() => {
  warmGeneration += 1;
  heroGeneration += 1;
  window.removeEventListener("resize", measure);
  galleryResizeObserver?.disconnect();
  window.removeEventListener("online", retryMedia);
  document.removeEventListener("visibilitychange", onGalleryVisibilityChange);
  window.removeEventListener("mousemove", onMouseMove);
  window.removeEventListener("mouseup", onPointerUp);
  track.value?.removeEventListener("scrollend", onScrollSettle);
  galleryRoot.value?.removeEventListener("touchmove", preventPanScroll);
  if (settleTimer) clearTimeout(settleTimer);
  cancelWarmSchedule();
  galleryVisibilityObserver?.disconnect();
  galleryVisibilityObserver = null;
});

// Treat items as the SAME product unless their structural identity changes, and
// key video items by their stable `videoUrl` rather than their poster. Posters
// are now static CDN URLs so they no longer churn, but a poster that falls back
// to the product photo still must not be read as "a different product" and yank
// the visitor back to slide 0 mid-browse.
const itemsSignature = (items: GalleryItem[]) =>
  items.length +
  ":" +
  items.map((it) => it?.videoUrl || it?.image || it?.src || "").join("|");

let lastItemsSignature = "";
let lastHeroKey = "";

watch(
  () => props.items,
  async (items) => {
    const list = items || [];
    const signature = itemsSignature(list);
    const isNewProduct = signature !== lastItemsSignature;
    const isFirstProduct = !lastItemsSignature;
    lastItemsSignature = signature;
    const heroKey = isVideoItem(list[0]) ? "" : legacyPinKey(list[0]);
    // The same product's full answer replacing the catalogue preview list
    // (more photos, another `?v=`) leads with the photo slide one already
    // shows: only the other slides start again. Re-creating the hero hid it
    // until it decoded a second time, a blink right after the shared zoom.
    const keepHero =
      isNewProduct &&
      !isFirstProduct &&
      !!heroKey &&
      heroKey === lastHeroKey &&
      activeIndex.value === 0;
    lastHeroKey = heroKey;
    const dropSlot = (index: number) => !keepHero || index !== 0;
    const dropSlots = (set: Set<number>) => {
      for (const index of [...set]) if (dropSlot(index)) set.delete(index);
    };
    const dropKeys = (record: Record<number, unknown>) => {
      for (const key of Object.keys(record))
        if (dropSlot(Number(key))) delete record[Number(key)];
    };

    if (isNewProduct) {
      // Different product (or first load): start from the first slide, and drop
      // every video back to "not requested" so the new product's clips are as
      // untouched as the previous one's were before anybody pressed play.
      resetAllVideos();
      videoRefs.clear();
      resetZoom();
      dragging.value = false;
      touchActive = false;
      scrollGesture = false;
      programmaticTarget = null;
      if (settleTimer) clearTimeout(settleTimer);
      settleTimer = null;
      if (snapSuspended) suspendSnap(false);
      cancelWarmSchedule();
      warmGeneration += 1;
      if (!keepHero) {
        heroGeneration += 1;
        heroInstant.value = false;
        heroReady = false;
        transitionPreviewVisible.value =
          Boolean(props.transitionPreviewSrc) && isFirstProduct;
        if (!isFirstProduct) heroFromServer.value = false;
      }
      mediaError.value = "";
      warmDirection = 1;
      [
        loadedMediaIndices,
        visitedMediaIndices,
        previewMediaIndices,
        previewPaintedMediaIndices,
        paintedMediaIndices,
        failedMediaIndices,
        originalSourceIndices,
        pendingMediaIndices,
      ].forEach(dropSlots);
      dropKeys(mediaRetries);
      dropKeys(posterOverrides);
      dropKeys(previewOverrides);
      activeIndex.value = 0;
      priorityMediaIndex.value = 0;
      // The hero slide is the LCP element and must carry a real src in the SSR
      // HTML — it bypasses the warm-up gate entirely.
      if (list.length > 0) {
        loadedMediaIndices.add(0);
        visitedMediaIndices.add(0);
      }
      const generation = warmGeneration;
      await nextTick();
      if (generation !== warmGeneration) return;
      measure();
      // Another product starts on its first photo. (The first product keeps
      // a swipe made before hydration: onMounted adopts it.)
      if (!isFirstProduct && track.value?.scrollLeft) {
        programmaticTarget = 0;
        programmaticSince = Date.now();
        programmaticMoved = false;
        track.value.scrollLeft = 0;
        scheduleSettle();
      }
      paint();
      const hero = galleryRoot.value?.querySelector<HTMLImageElement>(
        ".gallery-item:first-child .gallery-img:not(.gallery-transition-preview):not(.gallery-media-preview)"
      );
      if (hero?.complete && hero.naturalWidth > 1) onMediaLoad(0);
      await nextTick();
      applyVideoPlaybackForActiveIndex();
    } else {
      // Same product, items array merely rebuilt (e.g. the video poster resolved).
      // Keep the user exactly where they are; just refresh the load window.
      activeIndex.value = clamp(
        activeIndex.value,
        0,
        Math.max(0, list.length - 1)
      );
      if (heroReady) scheduleWarm();
    }
  },
  { immediate: true }
);

// Borrow only completed thumbnail downloads. They remain visible underneath
// an unfinished request, without downgrading an already-painted main photo.
watch(
  [() => props.thumbnailPreviews, () => props.items],
  () => {
    props.items.forEach((item, index) => {
      const key = item.videoUrl || item.image || item.src || "";
      const source = props.thumbnailPreviews[key];
      if (
        !source ||
        previewOverrides[index] === source ||
        previewPaintedMediaIndices.has(index) ||
        paintedMediaIndices.has(index)
      )
        return;
      previewOverrides[index] = source;
      previewMediaIndices.add(index);
    });
  },
  { deep: true, immediate: true }
);

// Re-prioritise any unfinished queue around the active slide. On normal
// connections compact galleries are usually already complete; long and
// constrained galleries advance their rolling window. The parent still
// receives thumbnail state.
watch(activeIndex, (idx) => {
  if (!canLoadMedia(idx)) prioritizeMedia(idx);
  scheduleWarm();
  emit("slide-change", { index: idx });
});

// Imperative navigation for external controls (e.g. desktop thumbnails).
const goTo = (index: number) => {
  const target = clamp(index, 0, Math.max(0, props.items.length - 1));
  if (zoomedIndex.value !== null) resetZoom();
  mediaError.value = "";
  prioritizeMedia(target);
  scrollToIndex(target);
};

defineExpose({ goTo });
</script>
